import apiClient from '../../shared/auth/services'

// Helper function to make API calls
const apiCall = async (endpoint, data = {}) => {
  try {
    const response = await apiClient.post(endpoint, data)
    return response.data
  } catch (error) {
    console.error(`API call failed for ${endpoint}:`, error)
    throw error
  }
}

// Helper function to get MIME type based on file extension
const getMimeType = (extension) => {
  if (!extension) return 'application/octet-stream'
  
  const ext = extension.toLowerCase()
  const mimeTypes = {
    'pdf': 'application/pdf',
    'doc': 'application/msword',
    'docx': 'application/vnd.openxmlformats-officedocument.wordprocessingml.document',
    'xls': 'application/vnd.ms-excel',
    'xlsx': 'application/vnd.openxmlformats-officedocument.spreadsheetml.sheet',
    'ppt': 'application/vnd.ms-powerpoint',
    'pptx': 'application/vnd.openxmlformats-officedocument.presentationml.presentation',
    'txt': 'text/plain',
    'rtf': 'application/rtf',
    'zip': 'application/zip',
    'rar': 'application/x-rar-compressed',
    '7z': 'application/x-7z-compressed'
  }
  
  return mimeTypes[ext] || 'application/octet-stream'
}

// Helper function for file uploads
const uploadFile = async (endpoint, formData) => {
  try {
    const response = await apiClient.post(endpoint, formData, {
      headers: {
        'Content-Type': 'multipart/form-data',
      },
    })
    return response.data
  } catch (error) {
    console.error(`File upload failed for ${endpoint}:`, error)
    throw error
  }
}

export const satsueiDataTorokuService = {
  // Object management
  getObject: (objectId, satsueiMemoCategoryId = null, changeSatsueiMemoFlg = false) => {
    const data = { objectId }
    if (satsueiMemoCategoryId !== null) {
      data.zsatsueiMemoCategoryId = satsueiMemoCategoryId
    }
    if (changeSatsueiMemoFlg) {
      data.changeSatsueiMemoFlg = changeSatsueiMemoFlg
    }
    return apiCall('/getObject', data)
  },
  getObjectCount: (objectId) => apiCall('/getObjectCount', { objectId }),
  getMaxObjectId: () => apiCall('/getMaxObjectId'),
  getObjectId4PrevNext: (objectId, kbn) => apiCall('/getObjectId4PrevNext', { objectId, kbn }),
  addObject: (data) => apiCall('/addObject', data),
  updateObject: (data) => apiCall('/updateObject', data),
  deleteObject: (objectId) => apiCall('/deleteObject', { objectId }),
  copyObjectData: (data) => apiCall('/copyObjectData', data),

  // Category and guide management
  getSatsueiMemoCategory: (data = {}) => apiCall('/getSatsueiMemoCategory', data),
  getSatsueiGuideList: (satsueiMemoCategoryId) => apiCall('/getSatsueiGuideList', { satsueiMemoCategoryId }),
  getKyotenList: () => apiCall('/getKyotenList'),
  getImageFolderList: (objectId, isContainMishitei = true) =>
    apiCall('/getImageFolderList', { objectId, isContainMishitei }),

  // Form management
  getSatsueiMemoForm: (objectId, satsueiMemoCategoryId, changeSatsueiMemoFlg = false) =>
    apiCall('/getSatsueiMemoForm', { objectId, satsueiMemoCategoryId, changeSatsueiMemoFlg }),

  // Image management
  getImageList: (objectId, imageFolderId = null) => {
    const data = { objectId }
    if (imageFolderId !== null && imageFolderId !== 99) {
      data.imageFolderId = imageFolderId
    }
    return apiCall('/getObjectImageList', data)
  },
  uploadImage: (formData) => uploadFile('/uploadImage', formData),
  uploadFile: (endpoint, formData) => uploadFile(endpoint, formData),
  removeImage: (objectId, objectImageId) => apiCall('/removeImage', { objectId, objectImageId }),

  // Movie management
  getMovieList: (objectId) => apiCall('/getMovieList', { objectId }),
  uploadMovie: (formData) => uploadFile('/uploadMovie', formData),
  removeMovie: (objectId, objectMovieId) => apiCall('/removeMovie', { objectId, objectMovieId }),
  getMovie: (objectId, objectMovieId, filenameExtension) => {
    return apiClient.post('/movie', {
      objectId,
      objectMovieId,
      filenameExtension
    }, {
      responseType: 'blob',
    })
  },
  
  // Legacy method for backward compatibility
  getMovieContentUrl: (objectId, objectMovieId, filenameExtension) => {
    return apiClient.post('/movie', {
      objectId,
      objectMovieId,
      filenameExtension
    }, {
      responseType: 'blob',
    }).then(response => {
      const blob = new Blob([response.data], { type: 'video/mp4' })
      return URL.createObjectURL(blob)
    })
  },

  // Other file management
  getOtherFileList: (objectId) => apiCall('/getOtherFileList', { objectId }),
  uploadOtherFile: (formData) => uploadFile('/uploadOtherFile', formData),
  removeOtherFile: (objectId, objectOtherFileId) => apiCall('/removeOtherFile', { objectId, objectOtherFileId }),
  getOtherFileContent: (objectId, objectOtherFileId, filenameExtension) => {
    return apiClient.post('/getOtherFileName', {
      objectId,
      objectOtherFileId,
      filenameExtension
    }, {
      responseType: 'blob',
    })
  },
  
  // Legacy method for backward compatibility
  getOtherFileContentUrl: (objectId, objectOtherFileId, filenameExtension) => {
    return apiClient.post('/getOtherFileName', {
      objectId,
      objectOtherFileId,
      filenameExtension
    }, {
      responseType: 'blob',
    }).then(response => {
      const mimeType = getMimeType(filenameExtension)
      const blob = new Blob([response.data], { type: mimeType })
      return URL.createObjectURL(blob)
    })
  },

  // Image editing
  getMaxImageEditId: (objectId, objectImageId) => apiCall('/getMaxImageEditId', { objectId, objectImageId }),
  getImageShapeInfo: (objectId, objectImageId, objectImageEditId) =>
    apiCall('/getImageShapeInfo', { objectId, objectImageId, objectImageEditId }),
  getImageTextInfo: (objectId, objectImageId, objectImageEditId) =>
    apiCall('/getImageTextInfo', { objectId, objectImageId, objectImageEditId }),
  registImageShapeInfo: (data) => apiCall('/registImageShapeInfo', data),
  registImageTextInfo: (data) => apiCall('/registImageTextInfo', data),
  uploadEditedImage: (formData) => uploadFile('/uploadImage', formData),

  // Image tags
  getObjectImageTag: (objectId, objectImageId) => apiCall('/getObjectImageTag', { objectId, objectImageId }),
  addObjectImageTag: (data) => apiCall('/addObjectImageTag', data),
  getImageTagCategoryListByImageTag: (satsueiMemoCategoryId, inputImageTag) =>
    apiCall('/getImageTagCategoryListByImageTag', { satsueiMemoCategoryId, inputImageTag }),

  // Stamp management
  getStampList: () => apiCall('/getStampList'),

  // Composite image
  compositeImage: (data) => apiCall('/compositeImage', data),

  // Object sharing
  addObjectShare: (data) => apiCall('/addObjectShare', data),

  // Download
  getObjectListDownload: (data) => {
    return apiClient.post('/getObjectListDownload', data, {
      responseType: 'blob',
    })
  },

  // Image fetching - returns blob data for proper cleanup
  // imageType: optional 'satsueiImage' for full image (e.g. carousel); default 'thumb_comp'
  getImage: async (objectId, objectImageId, objectImageEditId, filenameExtension, isUploaded, imageType) => {
    try {
      const type = imageType === 'satsueiImage' ? 'satsueiImage' : 'thumb_comp'
      const response = await apiClient.get('/getImage', {
        params: {
          objectId,
          objectImageId,
          objectImageEditId,
          filenameExtension,
          isUploaded,
          type,
        },
        responseType: 'blob',
        headers: { Accept: 'image/jpeg' }
      })
      return response.data // Return blob data instead of URL
    } catch (error) {
      console.error('Failed to get image blob:', error)
      return null
    }
  },

  // Legacy method that returns URL (deprecated - use getImage with useBlobUrl hook instead)
  getImageUrl: async (objectId, objectImageId, objectImageEditId, filenameExtension, isUploaded) => {
    try {
      const response = await apiClient.get('/getImage', {
        params: {
          objectId,
          objectImageId,
          objectImageEditId,
          filenameExtension,
          isUploaded,
          type: 'thumb_comp',
        },
        responseType: 'blob',
        headers: { Accept: 'image/jpeg' }
      })
      return URL.createObjectURL(response.data)
    } catch (error) {
      console.error('Failed to get image URL:', error)
      return null
    }
  },

  async getImageBlobForEdit(objectId, objectImageId, objectImageEditId, filenameExtension) {
    try {
      const params = {
        objectId,
        objectImageId,
        objectImageEditId,
        filenameExtension,
        type: 'satsueiImage',
        time: new Date().getTime()
      }

      const response = await apiClient.get('/getImage', {
        params,
        responseType: 'blob',
        headers: { Accept: 'image/jpeg' }
      })
      return response
    } catch (error) {
      console.error('Failed to get image blob:', error)
      throw error
    }
  },

  getStampImageUrl: (stampCd, filenameExtension) => {
    const params = new URLSearchParams({
      type: 'stamp',
      filename: `${stampCd}.${filenameExtension}`,
      time: new Date().getTime()
    })
    return `${apiClient.defaults.baseURL}/api/getImage?${params}`
  },

  /**
   * Fetch a presigned S3 URL for an image so external apps (e.g. PIX Client print) can fetch it.
   * Calls /getImagePresignedUrl and returns the presigned URL.
   * @param {number} objectId
   * @param {object} image - { objectImageId, objectImageEditId, maxObjectImageEditId, filenameExtension, isUploaded }
   * @returns {Promise<string>} Presigned absolute URL to the image
   */
  getImageAbsoluteUrl: async (objectId, image) => {
    const objectImageEditId = image.objectImageEditId ?? image.maxObjectImageEditId ?? null
    const body = {
      objectId: String(objectId),
      objectImageId: String(image.objectImageId),
      ...(objectImageEditId != null && { objectImageEditId: String(objectImageEditId) }),
      filenameExtension: image.filenameExtension || '',
      isUploaded: image.isUploaded != null ? String(image.isUploaded) : '1',
      type: 'satsueiImage'
    }
    const response = await apiClient.post('/getImagePresignedUrl', body)
    const data = response.data
    return data?.presignedUrl ?? ''
  },

  updateImageOrderIndex: async (objectId, imageList) => {
    try {
      const response = await apiClient.post('/updateImageOrderIndex', {
        objectId,
        updateImageOrderIndexs: imageList.map((image, index) => ({
          objectImageId: image.objectImageId,
          orderIndex: index
        }))
      })
      return response.data
    } catch (error) {
      console.error('Failed to update image order:', error)
      throw error
    }
  },

  updateMovieOrderIndex: async (objectId, movieList) => {
    try {
      const response = await apiClient.post('/updateMovieOrderIndex', {
        objectId,
        updateMovieOrderIndexs: movieList.map((movie, index) => ({
          objectMovieId: movie.objectMovieId,
          orderIndex: index
        }))
      })
      return response.data
    } catch (error) {
      console.error('Failed to update movie order:', error)
      throw error
    }
  },

  updateOtherFileOrderIndex: async (objectId, otherFileList) => {
    try {
      const response = await apiClient.post('/updateOtherFileOrderIndex', {
        objectId,
        updateOtherFileOrderIndexs: otherFileList.map((otherFile, index) => ({
          objectOtherFileId: otherFile.objectOtherFileId,
          orderIndex: index
        }))
      })
      return response.data
    } catch (error) {
      console.error('Failed to update other file order:', error)
      throw error
    }
  },

  // Camera management methods (placeholder for future backend integration)
  getCameraList: () => apiCall('/getCameraList', {}),
  registerCamera: (data) => apiCall('/registerCamera', data),
  deleteCamera: (cameraId) => apiCall('/deleteCamera', { cameraId }),
  captureCameraImage: (cameraId, objectId) => apiCall('/captureCameraImage', { cameraId, objectId }),
  saveCameraImages: (objectId, images) => apiCall('/saveCameraImages', { objectId, images })
}
